import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Active-wallet context — injects the frontend's persisted active-wallet
 * Zustand store state into `useActiveWallet()` so the SDK hook doesn't
 * need to import `localStorage`-backed Zustand middleware directly.
 *
 * Platform-neutral: pure React context. No web/RN globals. Safe in
 * `src/auth/` core per `.claude/rules/platform-agnostic.md`.
 *
 * Wiring: the frontend mounts an `<ActiveWalletProvider>` inside
 * `SDKProvider` (or any wrapper above the first `useActiveWallet()` call):
 *
 * ```tsx
 * // In frontend/src/providers/active-wallet-bridge.tsx
 * const { activeWalletId, setActiveWalletId } = useActiveWalletStore();
 * return (
 *   <ActiveWalletProvider value={{ activeWalletId, isHydrated, setActiveWalletId }}>
 *     {children}
 *   </ActiveWalletProvider>
 * );
 * ```
 *
 * When no provider is mounted, the context returns `null`/no-op defaults
 * so components don't crash — the active wallet falls back to the first
 * wallet in the list.
 */
import { createContext, useContext } from 'react';
const DEFAULT_SLOT = Object.freeze({
    activeWalletId: null,
    isHydrated: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setActiveWalletId: (_id) => { },
});
// ── Context ──────────────────────────────────────────────────────────────────
const ActiveWalletContext = createContext(DEFAULT_SLOT);
/** Returns the active-wallet slot from the nearest `<ActiveWalletProvider>`. */
export function useActiveWalletSlot() {
    return useContext(ActiveWalletContext);
}
/**
 * Provides the hydrated persisted active-wallet id and its setter to
 * `useActiveWallet()` below in the tree.
 *
 * Mount once in the frontend provider stack, driven by `useActiveWalletStore`
 * from `@/stores/active-wallet`.
 */
export function ActiveWalletProvider({ value, children }) {
    return _jsx(ActiveWalletContext.Provider, { value: value, children: children });
}
//# sourceMappingURL=active-wallet-context.js.map